import React, { useCallback } from "react";
import {
  Box,
  Switch,
  Tooltip,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import LockIcon from "@mui/icons-material/Lock";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  sortByOrder,
  reorderByIndex,
  useDragReorder,
  dragRowSx,
  dragListContainerSx,
} from "./reorderUtils";
import SectionFieldsEditor from "./SectionFieldsEditor.js";
import { MANAGER_COLORS, accordionSx, helperCaptionSx } from "./managerTheme.js";

const isSimpleVisibility = (visible) =>
  !visible || visible.when === "always" || visible.when === "never";

const isVisible = (visible) => !visible || visible.when !== "never";

const SubSectionsEditor = ({ subSections, fieldSets, onChange, onFieldSetChange }) => {
  const sorted = sortByOrder(subSections);

  const updateSub = (id, patch) => {
    onChange(subSections.map((sub) => (sub.id === id ? { ...sub, ...patch } : sub)));
  };

  const handleSubReorder = useCallback(
    (dragIndex, dropIndex) => {
      onChange(reorderByIndex(subSections, dragIndex, dropIndex));
    },
    [subSections, onChange],
  );

  const { handleDragStart, handleDragOver, handleDrop } = useDragReorder(handleSubReorder);

  return (
    <Box>
      <Typography sx={helperCaptionSx}>Drag rows to reorder history sub-sections.</Typography>

      <Box sx={dragListContainerSx}>
        {sorted.map((sub, index) => {
          const simple = isSimpleVisibility(sub.visible);
          const fieldSet = sub.fieldSet ? fieldSets?.[sub.fieldSet] : null;

          return (
            <Box
              key={sub.id}
              sx={{
                borderBottom: index < sorted.length - 1 ? `1px solid ${MANAGER_COLORS.borderLight}` : "none",
                backgroundColor: MANAGER_COLORS.card,
              }}
            >
              <Box
                draggable
                onDragStart={() => handleDragStart(index)}
                onDragOver={handleDragOver}
                onDrop={() => handleDrop(index)}
                sx={{ ...dragRowSx, borderBottom: "none", flexWrap: "wrap" }}
              >
                <DragIndicatorIcon fontSize="small" sx={{ color: MANAGER_COLORS.primary, flexShrink: 0 }} />

                {simple ? (
                  <Tooltip title="Show/hide this sub-section">
                    <Switch
                      size="small"
                      checked={isVisible(sub.visible)}
                      onChange={(e) =>
                        updateSub(sub.id, {
                          visible: { when: e.target.checked ? "always" : "never" },
                        })
                      }
                    />
                  </Tooltip>
                ) : (
                  <Tooltip title="Has a conditional visibility rule. Reorder only from here.">
                    <Chip
                      size="small"
                      icon={<LockIcon fontSize="small" />}
                      label="Conditional"
                      sx={{ borderColor: MANAGER_COLORS.border }}
                    />
                  </Tooltip>
                )}

                <Typography variant="body2" sx={{ flex: 1, minWidth: 160, fontWeight: 500 }}>
                  {sub.label ?? sub.key}
                </Typography>
              </Box>

              {fieldSet && (
                <Accordion disableGutters elevation={0} sx={accordionSx}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: MANAGER_COLORS.primary }} />}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Fields — {sub.label ?? sub.key}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({sortByOrder(fieldSet.fields ?? []).length})
                      </Typography>
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ pt: 0, pb: 2, px: 2, bgcolor: MANAGER_COLORS.card }}>
                    <SectionFieldsEditor
                      fieldSetId={sub.fieldSet}
                      fieldSet={fieldSet}
                      onChange={onFieldSetChange}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SubSectionsEditor;
